<script lang="ts">
	import classNames from 'classnames';
	import dayjs from 'dayjs';
	import { PodcastCard } from '../../components/PodcastCard';
	import { PodcastEpisodeCard } from '../../components/PodcastEpisodeCard';
	import { TalksSpeakersCard } from '../../components/TalksSpeakersCard';
	import { TalksRoutes } from '../../constants/routes';
	import type { withPodcastPageTokens } from './utils/withPodcastPageTokens/withPodcastPageTokens';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { preserveGlobalNavCrumbs } from '#lib/features/navigation/utils/preserveGlobalNav/preserveGlobalNav.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { ApplePodcastsIcon } from '$global/assets/icons/ApplePodcastsIcon';
	import { SpotifyIcon } from '$global/assets/icons/SpotifyIcon';
	import { YouTubeIcon } from '$global/assets/icons/YouTubeIcon';
	import { Breadcrumbs } from '$global/components/Breadcrumbs';
	import { Button } from '$global/components/Button';
	import { Container } from '$global/components/Container';
	import { Markdown } from '$global/components/Markdown';
	import { Select, type SelectChangeEvent } from '$global/components/Select';
	import { Txt } from '$global/components/Txt';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import type { PodcastPageData } from '$routes/talks/podcasts/[slug]/types';

	const dataCyPrefix = 'podcast-page';

	let data = $derived(getPageData<PodcastPageData>(page.data));
	let podcast = $derived(data.podcast) as ReturnType<
		typeof withPodcastPageTokens
	>;
	let artistSlugs = $derived(data.artistSlugs);
	let sort = $derived(page.url.searchParams.get('sort') || 'newest');

	let crumbs = $derived(
		preserveGlobalNavCrumbs(
			[
				{ label: 'Home', href: GlobalRoutes.Home },
				{ label: 'Talks', href: GlobalRoutes.TalksHome },
				{
					label: 'Podcasts',
					href: TalksRoutes.Podcasts,
				},
				{
					label: podcast.title,
				},
			],
			page.url
		)
	);

	let newestSortedEpisodes = $derived(
		(podcast.episodes || []).sort((episodeA, episodeB) => {
			return (
				+new Date(episodeB?.publication_date) -
				+new Date(episodeA?.publication_date)
			);
		})
	);

	let sortedEpisodes = $derived(
		sort === 'newest'
			? newestSortedEpisodes
			: [...newestSortedEpisodes].reverse()
	);

	const handleChangeSort = (e: SelectChangeEvent) => {
		const url = new URL(page.url.href);
		url.searchParams.set('sort', e.detail.value);
		goto(url, { reset: false });
	};
</script>

<Breadcrumbs dataCy={dataCyPrefix} {crumbs} />

<div class="flex justify-center lg:hidden">
	<div class="relative h-[min(100vw,390px)] w-[min(100vw,390px)]">
		<img
			class="absolute h-full w-full object-cover"
			alt={podcast?.title}
			src={getImageUrl(podcast.image?.filename_disk)}
		/>
	</div>
</div>

<Container dataCy={dataCyPrefix} class="lg:hidden">
	<Txt
		dataCy={`${dataCyPrefix}-title`}
		variant="h3"
		component="h1"
		class="mt-6"
	>
		{podcast.title}
	</Txt>
	<Markdown
		class="mt-4"
		dataCy={`${dataCyPrefix}-description`}
		source={podcast.long_description}
		size="large"
	/>
</Container>

<hr class="lg:hidden" />

<Container dataCy={dataCyPrefix} class="lg:hidden">
	<div class="px-4 md:px-0">
		<Txt variant="label3" class="mt-4">Watch or listen on:</Txt>
		<div class="mt-3 flex flex-wrap gap-3 lg:flex-col">
			{#if podcast.youtube_link}
				<Button
					onclick={() => {
						window.open(
							podcast.youtube_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="h-10 min-w-full flex-1 justify-start [@media(min-width:500px)]:min-w-0"
					dataCy={`${dataCyPrefix}-youtube`}
					>{#snippet leading()}
						<YouTubeIcon
							hexCode="#FF0000"
							class="mr-2 !h-[26px] !w-[26px] scale-[1.2]"
						/>
					{/snippet}YouTube</Button
				>
			{/if}
			{#if podcast.spotify_link}
				<Button
					onclick={() => {
						window.open(
							podcast.spotify_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="h-10  flex-1 justify-start"
					dataCy={`${dataCyPrefix}-spotify`}
					>{#snippet leading()}
						<SpotifyIcon class="mr-2 !h-[26px] !w-[26px]" />
					{/snippet}Spotify</Button
				>
			{/if}
			{#if podcast.apple_podcasts_link}
				<Button
					onclick={() => {
						window.open(
							podcast.apple_podcasts_link as string,
							'_blank',
							'noreferrer noopener'
						);
					}}
					variant="secondary"
					size="md"
					icon
					class="h-10 flex-1 justify-start"
					dataCy={`${dataCyPrefix}-apple`}
					>{#snippet leading()}
						<ApplePodcastsIcon class="mr-2 !h-[26px] !w-[26px]" />
					{/snippet}Apple</Button
				>
			{/if}
		</div>
	</div>
</Container>

<Container dataCy={dataCyPrefix}>
	<Txt variant="h2" component="h1" class="my-6 hidden md:my-8 lg:block">
		{podcast.title}
	</Txt>
	<PodcastCard
		openpanelPrefix="TALKS/PODCAST_PAGE_PODCAST"
		link={false}
		dataCy={dataCyPrefix}
		{podcast}
		class="xs:border-0 hidden lg:flex"
		classes={{
			image: 'rounded-md',
			watchOrListen: 'lg:py-0 lg:-mt-1',
			info: 'lg:py-0',
		}}
	>
		<Markdown
			class="hidden lg:-mt-1 lg:block [&>p]:mb-4 [&>p:last-child]:mb-0"
			dataCy={`${dataCyPrefix}-description`}
			source={podcast.long_description}
			size="large"
		/>
		<div class="xs:flex-col mt-4 flex gap-4">
			<Txt variant="label4">
				{podcast.episodes_func?.count} episode{podcast.episodes_func?.count ===
				1
					? ''
					: 's'} &middot; Last updated {dayjs(
					newestSortedEpisodes[0]?.publication_date
				).format('D MMMM YYYY')}
			</Txt>
		</div>
	</PodcastCard>
</Container>

<hr class="mt-6 md:mt-10" />

<div class="bg-gray-100 pt-6 md:pt-10">
	<Container dataCy={dataCyPrefix}>
		<div class="xxs:flex-row mb-6 flex flex-col justify-between">
			<Txt variant="h2" component="h1" class="xxs:mb-0 mb-2">Episodes</Txt>
			<div class="flex flex-col items-end gap-2">
				<Select
					selectedPrefix="SORT:"
					dataCy={dataCyPrefix}
					name="sort"
					ariaLabel="Select a sort"
					options={[
						{ label: 'NEWEST', value: 'newest' },
						{ label: 'OLDEST', value: 'oldest' },
					]}
					value={sort}
					onchange={handleChangeSort}
					classes={{
						placeholder: 'font-medium tracking-widest',
					}}
				/>
				<Txt variant="body3" class="text-gray-500">
					{sortedEpisodes.length} result{sortedEpisodes.length === 1 ? '' : 's'}
				</Txt>
			</div>
		</div>

		<div class="flex flex-col gap-4 pb-6">
			{#each sortedEpisodes as podcastEpisode (podcastEpisode?.id)}
				<PodcastEpisodeCard {podcastEpisode} />
			{/each}
		</div>
	</Container>

	{#if podcast.podcast_hosts?.length}
		<hr class="mt-4 md:mt-8" />
	{/if}
</div>

{#if podcast.podcast_hosts?.length}
	<Container dataCy={dataCyPrefix}>
		<Txt variant="h4" class="my-6 md:my-8">About the hosts</Txt>

		<div class="max-w-[760px] pb-12">
			{#each podcast.podcast_hosts as s, i}
				{@const speaker = s?.HENI_Talks_Speakers_id}
				<TalksSpeakersCard
					podcastOpenpanelPrefix="TALKS/PODCAST_PAGE"
					{artistSlugs}
					hasTalks={(speaker?.HENI_Talks_Talks_func?.count ?? 0) > 0}
					name={`${speaker?.name}`}
					biography={s?.biographyTokens}
					professionalTitle={speaker?.professional_title}
					image={getImageUrl(`${speaker?.profile_image?.filename_disk}`)}
					twitter={speaker?.twitter_handle || ''}
					insta={speaker?.instagram_handle || ''}
					dataCy={dataCyPrefix}
					class={classNames({ 'mt-4': !!i })}
				/>
			{/each}
		</div>
	</Container>
{/if}
